import { DEMO_ADMIN, DEMO_COLLEAGUE, DEMO_MARKETING } from './seed/admin'
import { demoTeam } from './teamStore'
import { accessOf, can, viewerFor, type Access } from '../core/rules/permissions'
import type { Viewer } from '../core/rules/staffAccess'
import type { Permission, RoleId } from '../core/types/team'

/**
 * DEMO — wie er in dit tabblad in het beheer is ingelogd: Sarah, de voorbeeldcollega of de
 * marketeer. Per tabblad (sessionStorage), net als de beheersessie zelf. De server haalt dit uit de
 * sessie; de rollen komen van het account (demo/teamStore.ts), niet van de persoon.
 */
export type Identity = 'eigenaar' | 'collega' | 'marketing'
const KEY = 'bb-demo-admin-identity'

type Person = { staffId: string; name: string; email: string; password: string; role: RoleId }

/** De testaccounts, met de rol waarmee ze beginnen (Team en toegang kan die later aanpassen). */
export const DEMO_PEOPLE: Record<Identity, Person> = {
  eigenaar: { staffId: 'sarah', name: DEMO_ADMIN.name, email: DEMO_ADMIN.email, password: DEMO_ADMIN.password, role: 'eigenaar' },
  collega: { ...DEMO_COLLEAGUE, role: 'behandelaar-zzp' },
  marketing: { ...DEMO_MARKETING, role: 'marketing' },
}

const isIdentity = (v: unknown): v is Identity => typeof v === 'string' && v in DEMO_PEOPLE

export const demoAdminIdentity = {
  get(): Identity {
    try {
      const v = sessionStorage.getItem(KEY)
      return isIdentity(v) ? v : 'eigenaar'
    } catch {
      return 'eigenaar'
    }
  },
  set(who: Identity) {
    try {
      sessionStorage.setItem(KEY, who)
    } catch {
      /* niet erg: dan Sarah */
    }
  },
  /** Het testaccount van dit tabblad (e-mail en wachtwoord voor inloggen en opnieuw bevestigen). */
  person(): Person {
    return DEMO_PEOPLE[this.get()]
  },
  /** Wie klikte: persoon, naam en account. Voor "Afgesloten door …", wie een bon maakte en het logboek. */
  current(): { staffId: string; name: string; accountId: string } {
    const { staffId, name } = this.person()
    return { staffId, name, accountId: demoTeam.byStaff(staffId)?.accountId ?? `acc-${staffId}` }
  },
  roles(): RoleId[] {
    const account = demoTeam.byStaff(this.current().staffId)
    return account?.roles ?? [this.person().role]
  },
  access(): Access {
    return accessOf({ roles: this.roles(), personId: this.current().staffId })
  },
  can(p: Permission, row?: { staffId?: string }): boolean {
    return can(this.access(), p, row)
  },
  /** Wie het deed, voor `by` en `accountId` op een schrijfactie (voorraad, inkoop, telling). */
  actor(): { by: string; accountId: string } {
    const me = this.current()
    return { by: me.name, accountId: me.accountId }
  },
  /** Voor de bestaande regels (`ownOnly`, `kassaDayFor`, `bookingProblem`). */
  viewer(p: Permission): Viewer {
    return viewerFor(this.access(), p)
  },
}
